const TOTAL_STEPS = 6;
const MINIMUM_AGE = 18;
const UNAVAILABLE_STATES = new Set(['AK', 'MS', 'NJ']);

class TaTrtIntake extends HTMLElement {
  connectedCallback() {
    this.answers = {};
    this.product = this.readProduct();
    this.screens = Array.from(this.querySelectorAll('.ta-trt-intake__screen'));
    this.progress = this.querySelector('.ta-trt-intake__progress');
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.progressCurrent = this.querySelector('[data-progress-current]');
    this.checkoutPending = false;

    const dobYear = this.querySelector('[name="dob_year"]');
    if (dobYear) {
      const today = new Date();
      dobYear.max = String(today.getFullYear());
      dobYear.min = String(today.getFullYear() - 120);
    }

    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);

    const requestedStep = this.stepFromUrl();
    const initialStep = typeof requestedStep === 'number' && requestedStep > 1 ? 1 : requestedStep;
    this.render(initialStep, { replace: initialStep !== requestedStep });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  readProduct() {
    const element = this.querySelector('[data-product]');
    if (!element) return {};

    try {
      return JSON.parse(element.textContent) || {};
    } catch (_error) {
      return {};
    }
  }

  onSubmit = (event) => {
    const screen = event.target.closest('[data-step]');
    if (!screen || !this.contains(screen)) return;
    event.preventDefault();

    const step = Number(screen.dataset.step);
    const result = this.validate(screen);
    if (!result.valid) {
      this.showError(screen, result.error);
      return;
    }

    this.clearError(screen);
    if (result.unavailable) {
      this.showUnavailable(result.unavailable);
      return;
    }

    const nextStep = Math.min(step + 1, TOTAL_STEPS);
    if (nextStep === TOTAL_STEPS) this.populateReview();
    this.render(nextStep, { push: true });
  };

  onClick = (event) => {
    const button = event.target.closest('[data-action]');
    if (!button || !this.contains(button)) return;

    const action = button.dataset.action;
    if (action === 'back') {
      event.preventDefault();
      const previousStep = Math.max(1, Number(this.currentStep) - 1);
      this.render(previousStep, { push: true });
    }

    if (action === 'restart') {
      event.preventDefault();
      this.answers = {};
      this.querySelectorAll('form').forEach((form) => form.reset());
      this.render(1, { push: true });
    }

    if (action === 'checkout') {
      event.preventDefault();
      this.checkout(button);
    }
  };

  onPopState = () => {
    const step = this.stepFromUrl();
    if (typeof step === 'number' && step === TOTAL_STEPS) this.populateReview();
    this.render(step);
  };

  validate(screen) {
    const field = screen.dataset.field;

    if (field === 'dob') {
      const value = this.composeDate(
        screen.querySelector('[name="dob_year"]').value,
        screen.querySelector('[name="dob_month"]').value,
        screen.querySelector('[name="dob_day"]').value,
      );
      const birthDate = this.parseDate(value);
      if (!birthDate || birthDate > new Date()) {
        return { valid: false, error: 'Please enter a valid date of birth.' };
      }

      this.answers.dob = value;
      if (this.ageOnDate(birthDate, new Date()) < MINIMUM_AGE) {
        return {
          valid: true,
          unavailable: 'The online TRT assessment is only available to adults age 18 and older.',
        };
      }
      return { valid: true };
    }

    if (field === 'physical') {
      const feet = Number(screen.querySelector('[name="height_feet"]').value);
      const inchesValue = screen.querySelector('[name="height_inches"]').value;
      const inches = Number(inchesValue);
      const weight = Number(screen.querySelector('[name="weight"]').value);

      if (!feet || inchesValue === '' || !Number.isInteger(inches) || inches < 0 || inches > 11) {
        return { valid: false, error: 'Please enter your complete height.' };
      }
      if (!weight || weight < 70 || weight > 700) {
        return { valid: false, error: 'Please enter a weight between 70 and 700 pounds.' };
      }

      this.answers.height_feet = feet;
      this.answers.height_inches = inches;
      this.answers.weight = Math.round(weight);
      return { valid: true };
    }

    if (field === 'sex') {
      const choice = screen.querySelector('[name="sex"]:checked');
      if (!choice) return { valid: false, error: 'Please select an option.' };
      this.answers.sex = choice.value;
      return { valid: true };
    }

    if (field === 'state') {
      const select = screen.querySelector('[name="state"]');
      const option = select.selectedOptions[0];
      if (!select.value || !option) return { valid: false, error: 'Please select your state.' };

      this.answers.state = select.value;
      this.answers.state_code = option.dataset.code || '';
      if (UNAVAILABLE_STATES.has(this.answers.state_code)) {
        return {
          valid: true,
          unavailable: `The online TRT assessment is not currently available in ${select.value}.`,
        };
      }
      return { valid: true };
    }

    if (field === 'email') {
      const email = screen.querySelector('[name="email"]').value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return { valid: false, error: 'Please enter a valid email address.' };
      }
      this.answers.email = email;
      return { valid: true };
    }

    return { valid: true };
  }

  populateReview() {
    const values = {
      dob: this.formatDate(this.answers.dob),
      height:
        this.answers.height_feet != null
          ? `${this.answers.height_feet} ft ${this.answers.height_inches} in`
          : '',
      weight: this.answers.weight != null ? `${this.answers.weight} lb` : '',
      sex: this.answers.sex || '',
      state: this.answers.state || '',
      email: this.answers.email || '',
    };

    for (const [key, value] of Object.entries(values)) {
      const target = this.querySelector(`[data-review="${key}"]`);
      if (target) target.textContent = value;
    }
  }

  render(step, { push = false, replace = false } = {}) {
    const target =
      step === 'unavailable'
        ? this.querySelector('[data-screen="unavailable"]')
        : this.querySelector(`[data-step="${step}"]`);
    if (!target) {
      this.render(1, { replace: true });
      return;
    }

    this.currentStep = step;
    this.screens.forEach((screen) => {
      screen.hidden = screen !== target;
      screen.classList.remove('is-entering');
    });
    target.hidden = false;
    void target.offsetWidth;
    target.classList.add('is-entering');

    const numericStep = typeof step === 'number' ? step : 1;
    if (this.progress) this.progress.hidden = step === 'unavailable';
    if (this.progressBar) this.progressBar.style.width = `${(numericStep / TOTAL_STEPS) * 100}%`;
    if (this.progressCurrent) this.progressCurrent.textContent = String(numericStep);

    if (push || replace) this.syncUrl(step, { push, replace });
    this.focusScreen(target);
    window.scrollTo({ top: 0, behavior: this.reduceMotion() ? 'auto' : 'smooth' });
  }

  focusScreen(screen) {
    const focusTarget =
      screen.querySelector('input:not([type="radio"]), select') ||
      screen.querySelector('input[type="radio"]') ||
      screen.querySelector('h1, h2');

    if (!focusTarget) return;
    if (/^H[12]$/.test(focusTarget.tagName)) focusTarget.tabIndex = -1;
    window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
  }

  showUnavailable(message) {
    const target = this.querySelector('[data-unavailable-message]');
    if (target) target.textContent = message;
    this.render('unavailable', { push: true });
  }

  showError(screen, message) {
    const error = screen.querySelector('[data-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  clearError(screen) {
    const error = screen.querySelector('[data-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  }

  stepFromUrl() {
    const value = new URLSearchParams(window.location.search).get('step');
    if (value === 'unavailable') return 'unavailable';
    const step = Number(value);
    return Number.isInteger(step) && step >= 1 && step <= TOTAL_STEPS ? step : 1;
  }

  syncUrl(step, { push, replace }) {
    const url = new URL(window.location.href);
    if (step === 1) url.searchParams.delete('step');
    else url.searchParams.set('step', String(step));

    const method = replace ? 'replaceState' : push ? 'pushState' : null;
    if (method) window.history[method]({ step }, '', `${url.pathname}${url.search}`);
  }

  async checkout(button) {
    if (this.checkoutPending) return;

    const review = this.querySelector('[data-step="6"]');
    const variantId = Number(this.product.variantId);
    const label = button.querySelector('[data-checkout-label]') || button;
    const originalLabel = label.textContent;

    if (!variantId || !this.product.available) {
      this.showError(
        review,
        'This assessment is not available for checkout while it is under review. Please try again after it is published.',
      );
      return;
    }

    this.checkoutPending = true;
    button.disabled = true;
    label.textContent = 'Preparing checkout…';
    this.clearError(review);

    try {
      const cart = await this.fetchJson(this.dataset.cartUrl);
      const alreadyInCart = cart.items?.some((item) => Number(item.variant_id) === variantId);

      if (!alreadyInCart) {
        await this.fetchJson(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
      }

      await this.fetchJson(this.dataset.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: this.cartAttributes() }),
      });

      window.location.assign(this.dataset.checkoutUrl || '/checkout');
    } catch (_error) {
      this.checkoutPending = false;
      button.disabled = false;
      label.textContent = originalLabel;
      this.showError(review, 'We couldn’t prepare your checkout. Please try again.');
    }
  }

  async fetchJson(url, options) {
    const response = await fetch(url, options);
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.description || body.message || 'Request failed');
    return body;
  }

  cartAttributes() {
    return {
      'TRT Intake — DOB': this.answers.dob || '',
      'TRT Intake — Height': `${this.answers.height_feet || ''} ft ${this.answers.height_inches ?? ''} in`.trim(),
      'TRT Intake — Weight': this.answers.weight ? `${this.answers.weight} lb` : '',
      'TRT Intake — Sex assigned at birth': this.answers.sex || '',
      'TRT Intake — State': this.answers.state || '',
      'TRT Intake — Email': this.answers.email || '',
      'TRT Intake — Product': this.product.title || 'Testosterone Replacement Therapy',
    };
  }

  composeDate(year, month, day) {
    if (!/^\d{4}$/.test(String(year).trim()) || !month || !day) return '';
    return `${String(year).trim()}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  parseDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }
    return date;
  }

  ageOnDate(birthDate, date) {
    let age = date.getFullYear() - birthDate.getFullYear();
    const beforeBirthday =
      date.getMonth() < birthDate.getMonth() ||
      (date.getMonth() === birthDate.getMonth() && date.getDate() < birthDate.getDate());
    if (beforeBirthday) age -= 1;
    return age;
  }

  formatDate(value) {
    const date = this.parseDate(value);
    if (!date) return '';
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  }

  reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('ta-trt-intake')) {
  customElements.define('ta-trt-intake', TaTrtIntake);
}
